import React, { useState, useContext, useMemo } from 'react';

import { Row, Col, notification } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { isJobNonExtEvent } from '@libs/EventTypes';
import { datePickerToApi, getMoment } from '@libs/NwMoment';
import { validateAmountZeroAllowed } from '@libs/NwUtils';
import { getProjectDescriptionsOptions, getProjectTitlesOptions } from '@libs/ProjectUtils';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { useProjectContext } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { getContact } from '@hooks/API/Contact/UseContactsList';
import { forceReloadProject, onGetOrderedEventList } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadProjectStatements } from '@hooks/API/Project/UseProjectStatements';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import Switch from '@components/Gui/CustomSwitch';
import CustomSwitch from '@components/Gui/CustomSwitch';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import {
    NWAutoComplete,
    NWBookingEventSelectorForTransactions,
    NWDatePicker,
    NWSelect,
    NWTextAreaAutoComplete,
    NWContactWithBillingInfo,
    NWAmountInput,
    NWSwitch,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import {
    AddNewLink,
    ProjectTransactionFormContainer,
    UsageContainer,
} from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    getEventFirstConfirmedDate,
    getProjectModelsForJobFees,
    getSingleConfirmedJobEvent,
} from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';
import AddEditUsageDrawer from '@components/Project/ProjectTransactions/Usages/AddEditUsageDrawer';

import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import ModelSelectorByWorkDays from './Common/ModelSelectorByWorkDays';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const JobFeeForm = ({ onCloseDrawer, clientBillingInfos, contacts, width, minWidth }) => {
    const { cachedList } = useContext(ListContext);
    const contextData = useProjectContext();
    const { projectData: currentProject, transactionsGrouped } = contextData;

    const transactionTitles = [
        ...new Set(
            transactionsGrouped.flatMap((group) => group.Dates.flatMap((date) => date.Transactions.map((t) => t.Title)))
        ),
    ].filter(Boolean);

    const transactionDescriptions = [
        ...new Set(
            transactionsGrouped.flatMap((group) =>
                group.Dates.flatMap((date) => date.Transactions.map((t) => t.Description))
            )
        ),
    ].filter(Boolean);

    const transactionProperties = useTransactionLabels('JobFee');
    const getTransactionLabel = (transactionLabelID) =>
        transactionProperties.labels.find((label) => label.ID === transactionLabelID);
    const defaultTypeQuantity = transactionProperties.startLabel.TypeQuantityName
        ? transactionProperties.startLabel.TypeQuantityName
        : 'Units';

    const [showUsage, setShowUsage] = useState(false);
    const [usePayroll, setUsePayroll] = useState(false);
    const [showNewUsage, setShowNewUsage] = useState(false);
    const configurations = useConfigurations();

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;

    const [agencyFeeToApply, setAgencyFeeToApply] = useState(configurations.defaultAgencyFeePerc);

    const startGroupModelDateList = getProjectModelsForJobFees(currentProject, defaultTypeQuantity, false);
    const [groupedModelDates, setGroupedModelDates] = useState(startGroupModelDateList);

    const [{ areaContext }] = useStateValue();
    const events = onGetOrderedEventList(currentProject, areaContext, false, false);
    // Per JobFee solo eventi 'job' (no 'ext'): se ne esiste uno solo ed e'
    // confermato lo passiamo gia' selezionato al BookingEventSelectorForTransactions.
    const preselectedEvent = getSingleConfirmedJobEvent(events, isJobNonExtEvent);
    // Data di default per WorkDate (visibile solo con typeQuantity Units/Flat):
    // prima data confermata dell'evento preselezionato, se esiste.
    const preselectedConfirmedDate = preselectedEvent ? getEventFirstConfirmedDate(preselectedEvent) : null;
    const preselectedWorkDate = preselectedConfirmedDate ? getMoment(preselectedConfirmedDate) : null;

    const updateTransactionLabel = (transactionLabelID, currentTypeQuantity) => {
        //check if the transaction label typequantity is different from the current typequantity
        const transactionLabel = getTransactionLabel(transactionLabelID);
        const newTypeQuantity = transactionLabel.TypeQuantityName;
        if (newTypeQuantity !== currentTypeQuantity) {
            const newGroupModelDateList = getProjectModelsForJobFees(currentProject, newTypeQuantity, false);
            setGroupedModelDates(newGroupModelDateList);
        }
    };

    const updateTypeQuantity = (typeQuantity) => {
        const newGroupModelDateList = getProjectModelsForJobFees(currentProject, typeQuantity, false);
        setGroupedModelDates(newGroupModelDateList);
    };

    const updateGroupWorkDays = (typeQuantity, groupWorkDays) => {
        const newGroupModelDateList = getProjectModelsForJobFees(currentProject, typeQuantity, groupWorkDays);
        setGroupedModelDates(newGroupModelDateList);
    };

    const saveTransaction = async (values, setSubmitting) => {
        let apiData = {
            ProjectID: currentProject.ID,
            ClientID: values.ContactToBill.ContactID,
            ClientBillingInfoID: values.ContactToBill.ContactBillingInfoID,
            // Family: 'JobFee',
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            TypeQuantity: values.TypeQuantity === 'Flat' ? 'Units' : values.TypeQuantity,
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            UsageID: values.UsageID,
            IncludingAgencyFee: ['Flat', 'Days', 'HalfDays'].includes(values.TypeQuantity)
                ? values.IncludingAgencyFee
                : false,
            ClientAdvance: usePayroll ? values.ClientAdvance[1] : 0,
        };
        let errorMessage = '';
        let models = [];
        for (const groupedModel of groupedModelDates) {
            if (groupedModel.Active) {
                //TODO: rivedere la prima data confermata, deve valere anche se non ci sono date confermate
                const confirmedJobs = groupedModel.EventDates.filter(
                    (eventDate) =>
                        isJobNonExtEvent(eventDate.EventType) && eventDate.OptionNumber === 10 && !eventDate.Cancel
                );
                const allJobs = groupedModel.EventDates.filter((eventDate) => isJobNonExtEvent(eventDate.EventType));
                const refAndWorkDate =
                    confirmedJobs && confirmedJobs.length > 0 ? confirmedJobs[0].FromDate : allJobs[0].FromDate;

                if (values.TypeQuantity === 'Days' || values.TypeQuantity === 'HalfDays') {
                    if (groupedModel.Quantity > 0) {
                        models.push({
                            ModelID: groupedModel.ModelID,
                            ModelBillingInfoID: null,
                            EventID: null,
                            WorkDate: refAndWorkDate,
                            RefDate: refAndWorkDate,
                            Quantity: groupedModel.Quantity,
                        });
                    } else {
                        errorMessage = 'Quantity must be greater than 0 for selected models';
                    }
                } else if (values.TypeQuantity === 'Flat') {
                    // In Flat/Units la data e' globale e gestita dal NWDatePicker
                    // a fianco del selettore evento; fallback al refAndWorkDate
                    // del modello se non valorizzata.
                    const refDate = values.WorkDate ? datePickerToApi(values.WorkDate) : refAndWorkDate;
                    models.push({
                        ModelID: groupedModel.ModelID,
                        ModelBillingInfoID: null,
                        EventID: values.SelectedEvent ? values.SelectedEvent.ID : null,
                        WorkDate: null,
                        RefDate: refDate,
                        Quantity: 1,
                    });
                } else if (values.TypeQuantity === 'Units') {
                    if (groupedModel.Quantity > 0) {
                        const refDate = values.WorkDate ? datePickerToApi(values.WorkDate) : refAndWorkDate;
                        models.push({
                            ModelID: groupedModel.ModelID,
                            ModelBillingInfoID: null,
                            EventID: values.SelectedEvent ? values.SelectedEvent.ID : null,
                            WorkDate: null,
                            RefDate: refDate,
                            Quantity: groupedModel.Quantity,
                        });
                    } else {
                        errorMessage = 'Quantity must be greater than 0 for selected models';
                    }
                }
            } else {
                const activeEventDates = groupedModel.EventDates.filter((eventDate) => eventDate.Active);
                if (values.TypeQuantity === 'Hours') {
                    for (const eventDate of activeEventDates) {
                        if (eventDate.Quantity > 0) {
                            models.push({
                                ModelID: groupedModel.ModelID,
                                ModelBillingInfoID: null,
                                EventID: eventDate.EventID,
                                WorkDate: eventDate.FromDate,
                                RefDate: eventDate.FromDate,
                                Quantity: eventDate.Quantity,
                            });
                        } else {
                            errorMessage = 'Quantity must be greater than 0 for selected models';
                        }
                    }
                } else if (values.TypeQuantity === 'Days' || values.TypeQuantity === 'HalfDays') {
                    for (const eventDate of activeEventDates) {
                        models.push({
                            ModelID: groupedModel.ModelID,
                            ModelBillingInfoID: null,
                            EventID: eventDate.EventID,
                            WorkDate: eventDate.FromDate,
                            RefDate: eventDate.FromDate,
                            Quantity: 1,
                        });
                    }
                }
            }
        }
        if (errorMessage) {
            setSubmitting(false);
            notification.error({
                message: 'Error',
                description: errorMessage,
                placement: 'bottomRight',
                bottom: 100,
                duration: 0,
            });
        } else {
            if (models.length === 0) {
                setSubmitting(false);
                notification.error({
                    message: 'Error',
                    description: 'Please select at least one model',
                    placement: 'bottomRight',
                    bottom: 100,
                    duration: 0,
                });
            } else {
                apiData = {
                    ...apiData,
                    Models: [...models],
                };

                setSubmitting(false);
                try {
                    await Axios.post('/transactions/JobFee', apiData);
                    forceReloadProject(currentProject.ID);
                    forceReloadProjectAccountingDetails(currentProject.ID);
                    forceReloadProjectTransactions(currentProject.ID);
                    forceReloadProjectStatements(currentProject.ID);
                    onCloseDrawer();
                    setSubmitting(false);
                } catch (error) {
                    console.error('Create Project Rate Error => ', error);
                    setSubmitting(false);
                }
            }
        }
    };

    const selectedModelsCount = useMemo(() => {
        return [...groupedModelDates].filter(
            (model) => (model.Active && model.Quantity > 0) || model.EventDates.some((eventDate) => eventDate.Active)
        ).length;
    }, [groupedModelDates]);

    const clientBillingInfoDefault = clientBillingInfos.find((item) => item.isDefault);
    let clientBillingInfoDefaultID;
    if (clientBillingInfoDefault) {
        clientBillingInfoDefaultID = clientBillingInfoDefault.ID;
    } else {
        if (clientBillingInfoDefault && clientBillingInfoDefault.length > 0) {
            clientBillingInfoDefaultID = clientBillingInfoDefault[0].ID;
        }
    }

    const initialValues = {
        ContactToBill: { ContactID: currentProject.CustomerID, ContactBillingInfoID: clientBillingInfoDefaultID },
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: transactionProperties.startLabel.Label,
        Description: '',
        SelectedEvent: preselectedEvent,
        WorkDate: preselectedWorkDate,
        Amount: [configurations.defaultCurrency, null],
        TypeQuantity: defaultTypeQuantity,
        UsageID: null,
        IncludingAgencyFee: false,
        ClientAdvance: [configurations.defaultCurrency, null],
    };

    const typeQuantities = [
        { key: 'H', label: 'Hours', value: 'Hours' },
        { key: 'D', label: 'Days', value: 'Days' },
        { key: 'HD', label: 'Half-Days', value: 'HalfDays' },
        { key: 'F', label: 'Flat Rate', value: 'Flat' },
        { key: 'U', label: 'Units', value: 'Units' },
    ];

    const updateAgencyFeeToApply = (selectedClient) => {
        if (selectedClient && selectedClient.DC && selectedClient.DC.length) {
            if (selectedClient.DC[0].AF) {
                setAgencyFeeToApply(selectedClient.DC[0].AF);
                return true;
            }
        }
        setAgencyFeeToApply(configurations.defaultAgencyFeePerc);
    };

    return currentProject ? (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
                ContactToBill: Yup.string().required('Client is required'),
            })}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid, validateForm }) => {
                const currentTransactionLabel = transactionProperties.labels.find(
                    (label) => label.ID === values.TransactionLabelID
                );
                return (
                    <NwForm
                        values={values}
                        //onTouchForm={onChangeForm}
                        onFinish={handleSubmit}
                        layout="vertical"
                    >
                        <ProjectTransactionFormContainer>
                            <Row gutter={32}>
                                <Col span={isMobile ? 24 : 13} className="rate-form">
                                    <TransactionLabelsSelector
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        transactionProperties={transactionProperties}
                                        onChange={(value) => {
                                            updateTransactionLabel(value, values.TypeQuantity);
                                        }}
                                    />
                                    {currentProject.PrevisionalTransactions.length > 0 && (
                                        <div className="previsional">
                                            <div className="label">Estimated Fees</div>
                                            {currentProject.PrevisionalTransactions.map((transaction) => {
                                                const transactionLabel = getTransactionLabel(
                                                    transaction.TransactionLabelID
                                                );
                                                return (
                                                    <div className="transaction-item" key={transaction.ID}>
                                                        <div
                                                            className="amount"
                                                            onClick={() => {
                                                                setFieldValue(
                                                                    'TransactionLabelID',
                                                                    transactionLabel.ID
                                                                );
                                                                setFieldValue('Description', transaction.Description);
                                                                setFieldValue('Amount', [
                                                                    transaction.Currency,
                                                                    transaction.Amount,
                                                                ]);
                                                                setFieldValue(
                                                                    'TypeQuantity',
                                                                    transactionLabel.TypeQuantityName
                                                                );
                                                                validateForm();
                                                            }}
                                                        >
                                                            {`${transactionLabel.Label} - ${transaction.Amount} ${transaction.Currency}`}
                                                        </div>
                                                        <div className="description">{transaction.Description}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <Row gutter={16}>
                                        <Col span={isMobile ? 24 : 12}>
                                            <Field
                                                disabled={currentTransactionLabel.FixedTarif}
                                                component={NWAmountInput}
                                                allowZero={true}
                                                value={values.Amount}
                                                label="Amount"
                                                name="Amount"
                                                validate={validateAmountZeroAllowed}
                                                onAfterChange={(value) => {
                                                    if (value[0] !== configurations.defaultCurrency) {
                                                        setUsePayroll(false);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col span={isMobile ? 24 : 8}>
                                            <Field
                                                component={NWSelect}
                                                label="Per"
                                                name="TypeQuantity"
                                                disabled={currentTransactionLabel.FixedTarif}
                                                value={values.TypeQuantity}
                                                options={typeQuantities}
                                                onAfterChange={updateTypeQuantity}
                                            />
                                        </Col>
                                    </Row>
                                    {['Flat', 'Days', 'HalfDays'].includes(values.TypeQuantity) && (
                                        <div className="inclusive-agency-fee">
                                            <Field
                                                component={NWSwitch}
                                                text={`Activate If the Amount Includes Agency Fee (${agencyFeeToApply}%)`}
                                                checked={values.IncludingAgencyFee}
                                                name="IncludingAgencyFee"
                                            />
                                        </div>
                                    )}
                                    <div className="payroll">
                                        <CustomSwitch
                                            text={`Payroll / Client Advance`}
                                            checked={
                                                usePayroll &&
                                                !currentTransactionLabel.FixedTarif &&
                                                configurations.defaultCurrency === values.Amount[0]
                                            }
                                            disabled={
                                                currentTransactionLabel.FixedTarif ||
                                                configurations.defaultCurrency !== values.Amount[0]
                                            }
                                            onChange={(value) => setUsePayroll(value)}
                                        />
                                        {(currentTransactionLabel.FixedTarif ||
                                            configurations.defaultCurrency !== values.Amount[0]) && (
                                            <div className="payroll-not-allowed">
                                                not allowed if the fee is not in the default currency or if the fee is
                                                fixed
                                            </div>
                                        )}
                                        {usePayroll && (
                                            <div className="payroll-amount">
                                                <Field
                                                    disabled={currentTransactionLabel.FixedTarif}
                                                    component={NWAmountInput}
                                                    value={values.ClientAdvance}
                                                    label=""
                                                    name="ClientAdvance"
                                                    fixedCurrency={configurations.defaultCurrency}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {(values.TypeQuantity === 'Units' || values.TypeQuantity === 'Flat') && (
                                        <>
                                            <Field
                                                component={NWBookingEventSelectorForTransactions}
                                                label="Event"
                                                name="SelectedEvent"
                                                events={events}
                                                eventSelectedUniqID={
                                                    preselectedEvent ? preselectedEvent.uniqID : undefined
                                                }
                                                onAfterChange={(value) => {
                                                    if (value) {
                                                        const firstConfirmedDate = getEventFirstConfirmedDate(value);
                                                        if (firstConfirmedDate) {
                                                            setFieldValue('WorkDate', getMoment(firstConfirmedDate));
                                                        }
                                                    }
                                                }}
                                            />
                                            <Field
                                                component={NWDatePicker}
                                                label="Date"
                                                name="WorkDate"
                                                format="MMM DD, YYYY"
                                                allowClear={false}
                                                value={values.WorkDate}
                                            />
                                        </>
                                    )}
                                    <Field
                                        component={NWContactWithBillingInfo}
                                        fullSelector
                                        value={values.ContactToBill}
                                        label="Client"
                                        name="ContactToBill"
                                        contacts={contacts}
                                        billingInfos={clientBillingInfos}
                                        disabled={contacts.length === 1}
                                        required
                                        onAfterChange={() => {
                                            const selectedClient = getContact(values.ContactToBill.ContactID);
                                            updateAgencyFeeToApply(selectedClient);
                                        }}
                                    />
                                    <Field
                                        component={NWAutoComplete}
                                        label="Title"
                                        name="Title"
                                        value={values.Title}
                                        maxLength={255}
                                        options={[
                                            ...(transactionTitles.length > 0
                                                ? [
                                                      {
                                                          label: 'Other Transactions',
                                                          options: transactionTitles.map((title) => ({
                                                              label: title,
                                                              value: title,
                                                          })),
                                                      },
                                                  ]
                                                : []),
                                            ...getProjectTitlesOptions(currentProject),
                                        ]}
                                    />
                                    <Field
                                        component={NWTextAreaAutoComplete}
                                        label="Description"
                                        name="Description"
                                        type="text"
                                        value={values.Description}
                                        maxLength={2500}
                                        autoSize={{ minRows: 4, maxRows: 10 }}
                                        options={[
                                            ...(transactionDescriptions.length > 0
                                                ? [
                                                      {
                                                          label: 'Other Transactions',
                                                          options: transactionDescriptions.map((description) => ({
                                                              label: description,
                                                              value: description,
                                                          })),
                                                      },
                                                  ]
                                                : []),
                                            ...getProjectDescriptionsOptions(currentProject),
                                        ]}
                                    />
                                    <UsageContainer>
                                        <FlexContainer mg="0 0 8px">
                                            <Switch
                                                text="Usage"
                                                checked={showUsage}
                                                onChange={(value) => setShowUsage(value)}
                                            />
                                        </FlexContainer>
                                        {showUsage && currentProject.Usages.length > 0 && (
                                            <Field
                                                component={NWSelect}
                                                value={values.UsageID}
                                                // label="Usage"
                                                placeholder="Select an existing usage"
                                                name="UsageID"
                                                options={currentProject.Usages.map((el) => {
                                                    const usageTypeNames = el.UsageTypes.join(', ');
                                                    return {
                                                        key: el.ID,
                                                        value: el.ID,
                                                        label: `${usageTypeNames} - ${el.Duration}`,
                                                    };
                                                })}
                                            />
                                        )}
                                        {showUsage && (
                                            <AddNewLink onClick={() => setShowNewUsage(true)}>
                                                add new usage info
                                            </AddNewLink>
                                        )}
                                    </UsageContainer>
                                </Col>
                                <Col span={isMobile ? 24 : 11}>
                                    <ModelSelectorByWorkDays
                                        typeQuantity={values.TypeQuantity}
                                        models={groupedModelDates}
                                        updateModels={setGroupedModelDates}
                                        updateGroupWorkDays={updateGroupWorkDays}
                                    />
                                </Col>
                            </Row>
                        </ProjectTransactionFormContainer>
                        <NwFixedCancelAndSaveBar
                            isSubmitting={isSubmitting}
                            isValid={isValid && selectedModelsCount > 0}
                            onCancel={onCloseDrawer}
                            width={width}
                            minWidth={minWidth}
                        />
                        {showNewUsage && (
                            // @ts-ignore
                            <AddEditUsageDrawer
                                projectId={currentProject.ID}
                                usageTypes={cachedList.usageTypes.items}
                                onUpdate={async (newUsage) => {
                                    await forceReloadProject(currentProject.ID);
                                    setFieldValue('UsageID', newUsage.ID);
                                }}
                                onClose={() => setShowNewUsage(false)}
                            />
                        )}
                    </NwForm>
                );
            }}
        </Formik>
    ) : (
        <Loading />
    );
};

export default JobFeeForm;
